import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Archive, Search } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ArchivePage() {
  const [payrolls, setPayrolls] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear().toString());
  const { t } = useLanguage();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [payrollsRes, employeesRes] = await Promise.all([
          axios.get(`${API}/payroll`, { params: { status: 'finalized' } }),
          axios.get(`${API}/employees`)
        ]);
        setPayrolls(payrollsRes.data);
        setEmployees(employeesRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error(t('errorOccurred'));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [t]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3
    }).format(value || 0) + ' TND';
  };

  const years = [...new Set(payrolls.map(p => p.period_start.slice(0, 4)))].sort().reverse();

  const filteredPayrolls = payrolls.filter(p => {
    const matchesSearch = p.employee_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesEmployee = employeeFilter === 'all' || p.employee_id === employeeFilter;
    const matchesYear = p.period_start.startsWith(yearFilter);
    return matchesSearch && matchesEmployee && matchesYear;
  });

  // Group by month
  const groupedPayrolls = filteredPayrolls.reduce((acc, p) => {
    const month = p.period_start.slice(0, 7);
    if (!acc[month]) acc[month] = [];
    acc[month].push(p);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#E31E24] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="archive-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Archive className="w-8 h-8 text-[#E31E24]" />
        <h1 className="text-3xl font-bold text-[#1A1A1A]">{t('archive')}</h1>
      </div>

      {/* Filters */}
      <Card className="bg-white border-gray-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder={t('search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white border-gray-200 text-[#1A1A1A]"
                data-testid="search-input"
              />
            </div>
            <Select value={employeeFilter} onValueChange={setEmployeeFilter}>
              <SelectTrigger className="w-full sm:w-48 bg-white border-gray-200 text-[#1A1A1A]">
                <SelectValue placeholder={t('employees')} />
              </SelectTrigger>
              <SelectContent className="bg-white border-gray-200">
                <SelectItem value="all">{t('employees')}: All</SelectItem>
                {employees.map((emp) => (
                  <SelectItem key={emp.id} value={emp.id}>
                    {emp.first_name} {emp.last_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={yearFilter} onValueChange={setYearFilter}>
              <SelectTrigger className="w-full sm:w-32 bg-white border-gray-200 text-[#1A1A1A]">
                <SelectValue placeholder="Jahr" />
              </SelectTrigger>
              <SelectContent className="bg-white border-gray-200">
                {years.length === 0 ? (
                  <SelectItem value={new Date().getFullYear().toString()}>
                    {new Date().getFullYear()}
                  </SelectItem>
                ) : (
                  years.map((year) => (
                    <SelectItem key={year} value={year}>{year}</SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Archive List */}
      {Object.keys(groupedPayrolls).length === 0 ? (
        <Card className="bg-white border-gray-200">
          <CardContent className="py-12 text-center">
            <Archive className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-400">{t('noData')}</p>
          </CardContent>
        </Card>
      ) : (
        Object.entries(groupedPayrolls).sort((a, b) => b[0].localeCompare(a[0])).map(([month, monthPayrolls]) => (
          <Card key={month} className="bg-white border-gray-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-[#1A1A1A] text-lg">
                {new Date(month + '-01').toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}
                <Badge className="ml-3 badge-info">{monthPayrolls.length} Abrechnungen</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-gray-200 hover:bg-transparent">
                      <TableHead className="table-header">{t('employees')}</TableHead>
                      <TableHead className="table-header">{t('payrollPeriod')}</TableHead>
                      <TableHead className="table-header">{t('regularHours')}</TableHead>
                      <TableHead className="table-header">{t('totalGross')}</TableHead>
                      <TableHead className="table-header">{t('payoutGross')}</TableHead>
                      <TableHead className="table-header">Finalisiert</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {monthPayrolls.map((p) => (
                      <TableRow key={p.id} className="table-row" data-testid={`archive-row-${p.id}`}>
                        <TableCell className="text-[#1A1A1A]">{p.employee_name}</TableCell>
                        <TableCell className="text-gray-500 font-mono text-sm">
                          {p.period_start} - {p.period_end}
                        </TableCell>
                        <TableCell className="font-mono text-[#1A1A1A]">{p.regular_hours} h</TableCell>
                        <TableCell className="font-mono text-[#1A1A1A]">{formatCurrency(p.total_gross)}</TableCell>
                        <TableCell className="font-mono text-emerald-400">{formatCurrency(p.payout_gross)}</TableCell>
                        <TableCell className="text-gray-500 text-sm">
                          {p.finalized_at ? new Date(p.finalized_at).toLocaleDateString('de-DE') : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}
